import styled from 'styled-components'

import { Card, Layout, Text } from '@loadsmart/miranda-react'
import { toCSSValue } from '@loadsmart/miranda-react/dist/tokens'

import { RequestStatus } from 'core/domain/Request'

import { screen } from 'legacy/styles/screen'
import user from 'legacy/utils/user'

import { getShipmentTrackingStatus, type Shipment } from '../../domain/Shipment'
import { useProviderTrackingLink } from '../hooks/useProviderTrackingLink'
import { AssetCarrierCard } from './AssetCarrierCard'
import { AssignDriverCard } from './AssignDriverCard'
import DriverLocationForm from './DriverLocationForm'
import { LocationPings } from './LocationPings'
import { PreferredTrackingMethodCard } from './PreferredTrackingMethodCard'
import { useShipmentLocationsContext } from './ShipmentLocationsContext'
import { TrackingIndicator } from './TrackingIndicator'
import { TruckNumberCard } from './TruckNumberCard'

const StyledGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  gap: ${toCSSValue('spacing-4')};

  ${screen.md} {
    grid-template-columns: 2fr minmax(300px, 1fr);
  }
`
type TrackingVisibilityCardProps = {
  readonly shipment: Shipment
  readonly refetchShipment: (uuid: string) => Promise<void>
}

export function TrackingVisibilityCard({ shipment, refetchShipment }: TrackingVisibilityCardProps) {
  const { locations, getLocationsStatus } = useShipmentLocationsContext()
  const trackingStatus = getShipmentTrackingStatus(shipment, locations)
  const userIsLoggedIn = user.isLoggedIn()
  const { trackingLink } = useProviderTrackingLink(shipment.uuid)

  return (
    <Card collapsible>
      <Card.Title>
        <Layout.Group align="center" justify="space-between" style={{ width: '100%' }}>
          <Text variant="heading-md-bold">Provide tracking visibility to your shipper</Text>
          <TrackingIndicator
            status={trackingStatus}
            loading={getLocationsStatus === RequestStatus.Pending}
          />
        </Layout.Group>
      </Card.Title>
      <Card.Body
        style={{
          '--m-card-body-padding': `${toCSSValue('border-none')} ${toCSSValue(
            'spacing-6'
          )} ${toCSSValue('spacing-6')}`,
        }}
      >
        <Layout.Stack gap="spacing-2">
          {userIsLoggedIn && <PreferredTrackingMethodCard trackingLink={trackingLink} />}
          <StyledGrid>
            <Layout.Stack>
              <Layout.Switcher>
                <AssignDriverCard shipment={shipment} onDriverChanged={refetchShipment} />
                <TruckNumberCard shipment={shipment} onTruckChanged={refetchShipment} />
                <AssetCarrierCard shipment={shipment} onAssetCarrierAssigned={refetchShipment} />
              </Layout.Switcher>
              <Layout.Box
                borderWidth="border-thin"
                borderRadius="border-radius-m"
                padding="spacing-3"
                style={{ width: '100%' }}
              >
                <DriverLocationForm shipmentUUID={shipment.uuid} />
              </Layout.Box>
            </Layout.Stack>
            <LocationPings
              shipmentUUID={shipment.uuid}
              trackingLink={trackingLink}
              trackingStatus={trackingStatus}
            />
          </StyledGrid>
        </Layout.Stack>
      </Card.Body>
    </Card>
  )
}
